"use client";

import { SignedInHeaderActions } from "@/app/_components/SignedInHeaderActions";
import { SwooshBackdrop } from "@/app/_components/SwooshBackdrop";
import type { AuthUserInfo } from "@/auth";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { usePathname } from "next/navigation";
import MarbleHealthLogo from "public/MarbleHealth_Logo.png";

export default function NotAuthedLayout({
  children,
  authUserInfo, // If they are authed, we show the sign out button
  isImpersonating = false,
  hideHeader = false,
}: {
  children: React.ReactNode;
  authUserInfo?: AuthUserInfo;
  isImpersonating?: boolean;
  hideHeader?: boolean;
}) {
  const pathName = usePathname();
  const isStudentOnboarding = /\/students\/[^/]+\/onboarding/.test(
    pathName ?? "",
  );
  // The referral form sits on the Counselor Home swoosh, edge to edge.
  const isReferPage = /^\/refer(\/|$)/.test(pathName ?? "");

  return (
    <div
      className={cn(
        "relative isolate w-screen min-h-[calc(100dvh)] flex flex-col",
        // Clip the swoosh so it never makes a short page scroll.
        isReferPage
          ? "overflow-clip bg-white"
          : "bg-gradient-to-br from-[#EDF9FF] to-[#FFF0F0]",
      )}
    >
      {isReferPage && <SwooshBackdrop />}
      {/* If it's student onboarding or a compact warning page, don't render the header */}
      {/* In normal flow (not absolute) so the wordmark can never overlap the
          page's own heading on small screens. */}
      {!isStudentOnboarding && !hideHeader ? (
        <header className="flex h-16 w-full shrink-0 items-center justify-between px-4 md:px-8">
          <Image
            className="w-[168px] md:w-[220px]"
            src={MarbleHealthLogo}
            alt="Marble Health Logo"
            loading="lazy"
          />

          {authUserInfo ? (
            <SignedInHeaderActions
              authUserInfo={authUserInfo}
              isImpersonating={isImpersonating}
            />
          ) : null}
        </header>
      ) : null}
      {/* Short content (sign-in card, warning pages) centers in the space
          BELOW the in-flow header; tall content (booking) starts right under
          it and grows — centering can no longer push anything beneath the
          wordmark. */}
      {/* max-w-5xl: the widest public page is the referral form, whose Student
          Details section lays out three columns; the booking picker self-caps
          at md:max-w-3xl inside. */}
      <div
        className={cn("w-full", {
          "mx-auto flex max-w-5xl flex-1 flex-col items-center justify-center px-4 pb-16 md:px-8":
            !isStudentOnboarding,
        })}
      >
        {children}
      </div>
    </div>
  );
}
